import { create } from "zustand";
import type {
  Archive,
  AutoContinuePayload,
  PlayerCharacter,
  WeaponType,
} from "../domain/types";
import { WEAPON_TYPES } from "../domain/types";
import { emptyRegistry } from "../domain/itemRegistry";
import { archivePersistenceCoordinator } from "../services/archivePersistenceCoordinator";
import { finishPendingTurn } from "../services/db";
import { sendMessage } from "../services/sendMessage";
import type { FeatureRoutingSettings } from "../services/featureRoutes";
import { claimTask, abandonTask } from "../domain/tasks";
import { refreshTaskBoard } from "../features/task-board/service";
import { calculateDerived } from "../domain/equipmentStats";
import { repairArchive } from "../services/archiveRepair";
import {
  prepareRewindAndResend,
  rewindToMessage,
} from "../features/message-rewind/service";
import {
  profileFromCreation,
  type CreationInput,
} from "../features/character-creation/catalog";
import { recoverPendingTurns } from "../services/pendingTurnRecovery";
const proficiency = Object.fromEntries(
  WEAPON_TYPES.map((w) => [w, 0]),
) as Record<WeaponType, number>;
let activeController: AbortController | null = null;
export function createArchive(input: CreationInput): Archive;
export function createArchive(
  archiveName: string,
  characterName: string,
  origin: string,
): Archive;
export function createArchive(
  inputOrName: CreationInput | string,
  legacyName?: string,
  legacyOrigin?: string,
): Archive {
  const isLegacyCall = typeof inputOrName === "string";
  const input: CreationInput = isLegacyCall
    ? {
        archiveName: inputOrName,
        characterName: legacyName ?? "苍",
        gender: "male",
        raceId: "human",
        professionId: "hunter",
        birthplaceId: "kokoto",
        background: legacyOrigin ?? "来自旧大陆的新人猎人。",
        hook: "继续眼前的猎人旅程。",
      }
    : inputOrName;
  const now = Date.now();
  const profile = profileFromCreation(input);
  const pronouns = input.gender === "male" ? "他" : "她";
  const origin =
    isLegacyCall && legacyOrigin
      ? legacyOrigin
      : `${profile.birthplace.label} · ${profile.race.label} · ${profile.profession.label}`;
  const player: PlayerCharacter = {
    id: "B1",
    identity: { name: input.characterName, pronouns, origin },
    profile,
    hunter: { hr: 1, hrExp: 0 },
    vitals: { health: 100, stamina: 100 },
    wallet: { zenny: 1500, guildPoints: 0 },
    equipped: {
      weapon: null,
      head: null,
      chest: null,
      arms: null,
      waist: null,
      legs: null,
      charm: null,
    },
    proficiency: { ...proficiency },
    derived: {
      attack: 10,
      defense: 1,
      resistances: { fire: 0, water: 0, thunder: 0, ice: 0, dragon: 0 },
    },
  };
  return {
    archiveId: crypto.randomUUID(),
    name: input.archiveName,
    player,
    characters: {},
    monsters: {},
    itemRegistry: emptyRegistry(),
    tasks: {
      available: [],
      activeTaskId: null,
      history: [],
      lastRefreshDay: 0,
    },
    messages: [
      {
        id: crypto.randomUUID(),
        role: "system",
        content: `【开局档案】\n${input.characterName}，${profile.race.label}${profile.profession.label}，来自${profile.birthplace.label}。\n\n${profile.background}\n\n开局钩子：${profile.hook}`,
        createdAt: now,
      },
    ],
    narrativeMemory: { summary: "", facts: [], lastCompressedTurn: 0 },
    deepSummary: {
      text: "",
      coveredThroughMessageId: null,
      coveredTurns: 0,
      updatedAt: null,
      revision: 0,
    },
    worldEvents: [],
    scene: { location: profile.birthplace.label },
    gameTime: { day: 1, hour: 8 },
    pendingCraftingContext: null,
    pendingHuntContext: null,
    summaries: [],
    openingContext: `性别=${input.gender === "male" ? "男" : "女"}\n种族=${profile.race.label}，相对指数：血量${profile.race.indices.health}、耐力${profile.race.indices.stamina}、闪避${profile.race.indices.dodge}、攻击${profile.race.indices.attack}\n职业=${profile.profession.label}，猎人资格=${profile.profession.hasHunterLicense ? "有" : "无"}\n出生地=${profile.birthplace.label}\n背景=${profile.background}\n开局钩子=${profile.hook}`,
    quickChat: { conversations: {}, pendingConversationIds: [] },
    plotEvolution: {
      guidance: "",
      guidanceUpdatedTurn: 0,
      directorLedger: "",
      currentDirective: "",
      updatedAt: null,
    },
    meta: { createdAt: now, updatedAt: now, version: 4 },
  };
}
interface State {
  archive: Archive | null;
  streaming: string;
  busy: boolean;
  error: string | null;
  phase: string | null;
  lastInput: string;
  recoveryNotice: string | null;
  setArchive: (a: Archive) => Promise<void>;
  loadArchive: (a: Archive) => Promise<void>;
  dismissRecoveryNotice: () => void;
  newGame: (input: CreationInput) => Promise<void>;
  send: (
    input: string,
    s: FeatureRoutingSettings,
    autoContinue?: AutoContinuePayload,
  ) => Promise<void>;
  abort: () => void;
  retry: (s: FeatureRoutingSettings) => Promise<void>;
  claim: (id: string) => Promise<void>;
  abandon: () => Promise<void>;
  refreshTasks: (force?: boolean) => Promise<void>;
  equip: (key: string) => Promise<void>;
  rewind: (messageId: string) => Promise<void>;
  resend: (
    messageId: string,
    settings: FeatureRoutingSettings,
    editedContent?: string,
  ) => Promise<void>;
}
export const useGameStore = create<State>((set, get) => ({
  archive: null,
  streaming: "",
  busy: false,
  error: null,
  phase: null,
  lastInput: "",
  recoveryNotice: null,
  setArchive: async (archive) => {
    const repaired = repairArchive(archive).archive;
    await archivePersistenceCoordinator.enqueue(repaired);
    set({ archive: repaired, error: null });
  },
  loadArchive: async (archive) => {
    const repaired = repairArchive(archive).archive;
    const recovery = await recoverPendingTurns(repaired);
    await archivePersistenceCoordinator.enqueue(recovery.archive);
    set({ archive: recovery.archive, error: null, recoveryNotice: recovery.notices.join(" ") || null });
  },
  dismissRecoveryNotice: () => set({ recoveryNotice: null }),
  newGame: async (input) => get().setArchive(createArchive(input)),
  send: async (input, settings, autoContinue) => {
    const archive = get().archive;
    if (!archive || get().busy) return;
    activeController = new AbortController();
    set({
      busy: true,
      streaming: "",
      error: null,
      phase: "准备回合",
      lastInput: input.trim(),
    });
    try {
      const result = await sendMessage(
        archive,
        input,
        settings,
        (t) => set((s) => ({ streaming: s.streaming + t })),
        activeController.signal,
        async (committed) => get().setArchive(committed),
        {
          autoContinue,
          onPhase: (phase) => set({ phase }),
        },
      );
      await get().setArchive(result.archive);
      await finishPendingTurn(result.turnId);
      set({ streaming: "", busy: false, phase: null });
    } catch (e) {
      const aborted = e instanceof DOMException && e.name === "AbortError";
      set({
        busy: false,
        streaming: "",
        phase: null,
        error: aborted
          ? "本次生成已取消；已保存的正文不会回滚。"
          : e instanceof Error
            ? e.message
            : String(e),
      });
    } finally {
      activeController = null;
    }
  },
  abort: () => activeController?.abort(),
  retry: async (settings) => {
    const input = get().lastInput;
    if (input) await get().send(input, settings);
  },
  claim: async (id) => {
    const a = get().archive;
    if (a) await get().setArchive(claimTask(a, id));
  },
  abandon: async () => {
    const a = get().archive;
    if (a) await get().setArchive(abandonTask(a));
  },
  refreshTasks: async (force) => {
    const a = get().archive;
    if (a) await get().setArchive(await refreshTaskBoard(a, force));
  },
  equip: async (key) => {
    const a = get().archive;
    if (!a) return;
    const d = a.itemRegistry.definitions[key];
    if (!d || !["weapon", "armor", "charm"].includes(d.meta.kind)) return;
    const next = structuredClone(a);
    if (d.meta.kind === "weapon")
      next.player.equipped.weapon = { itemKey: key };
    else if (d.meta.kind === "armor")
      next.player.equipped[d.meta.slot] = { itemKey: key };
    else next.player.equipped.charm = { itemKey: key };
    next.player.derived = calculateDerived(next.player, next.itemRegistry);
    await get().setArchive(next);
  },
  rewind: async (messageId) => {
    const archive = get().archive;
    if (!archive || get().busy) return;
    set({ busy: true, error: null, phase: "正在回溯" });
    try {
      const restored = await rewindToMessage(archive, messageId);
      const repaired = repairArchive(restored).archive;
      set({ archive: repaired, busy: false, phase: null });
    } catch (error) {
      set({
        busy: false,
        phase: null,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  },
  resend: async (messageId, settings, editedContent) => {
    const archive = get().archive;
    if (!archive || get().busy) return;
    set({ busy: true, error: null, phase: "正在准备重生成" });
    try {
      const prepared = await prepareRewindAndResend(
        archive,
        messageId,
        editedContent,
      );
      const repaired = repairArchive(prepared.archive).archive;
      set({ archive: repaired, busy: false, phase: null });
      await get().send(
        prepared.replay.content,
        settings,
        prepared.replay.autoContinue,
      );
    } catch (error) {
      set({
        busy: false,
        phase: null,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  },
}));
